import React, { useEffect, useState } from 'react';

import { Button, Navbar } from "@material-tailwind/react";
import { HiMenu } from 'react-icons/hi';
import { useNavigate, Link } from 'react-router-dom';

import { useAuth } from '@components/navigation/Authentication.jsx';
import { ProjectsListProvider } from '@datahooks/ProjectsListContext.jsx';

import SideBar from '@components/navigation/SideBar.jsx';
import SpinningTitle from "@components/general/SpinningTitle.jsx";
import UserMenu from "@components/navigation/UserMenu.jsx";
import Footer from "@components/navigation/Footer.jsx";
import LoadingIcon from "@components/navigation/LoadingIcon.jsx";
import { PROJECT_SCOPE } from '@home/config';
import MetriqLogoSVG from "@components/navigation/Logo/MetriqLogoSVG.jsx";

import PropTypes from 'prop-types';

function AuthenticatedLayout({ children }) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [authProcessed, setAuthProcessed] = useState(false);
    const { user, isInitialised, signOut, acquireTokenSilent, initialAuthComplete } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (isInitialised && initialAuthComplete && !authProcessed) {
            if (user) {
                // Validate the token by calling acquireTokenSilent
                acquireTokenSilent([PROJECT_SCOPE])
                    .then(() => {
                        // Token is valid, allow navigation
                        return null;
                    })
                    .catch((error) => {
                        console.error('AuthenticatedLayout: Token is invalid or expired:', error);
                        signOut();  // Sign out the user if token is invalid
                    });
            } else {
                sessionStorage.setItem('postLoginRedirect', globalThis.location.pathname + globalThis.location.search);
                navigate('/'); // Redirect to login if not authenticated
            }
            setAuthProcessed(true);
        }
    }, [user, isInitialised, navigate, acquireTokenSilent, signOut, initialAuthComplete, authProcessed]);

    if (!isInitialised || (!user && !authProcessed)) {
        // Show loading icon while checking user authentication
        return (
            <div className="flex items-center justify-center h-screen w-screen">
                <LoadingIcon size={150} showText />
            </div>
        );
    }

    return (
        <ProjectsListProvider>
            <div className="min-h-screen dark:bg-dark-tremor-background-muted bg-white">
                <Navbar shadow={false} blurred={false} data-testid="authenticated-navbar"
                    className="flex items-center border-b dark:border-dark-tremor-border rounded-none justify-between top-0 relative w-full max-w-full bg-transparent px-2 py-0 my-0 lg:px-4 lg:py-0 "
                    style={{ position: 'relative', zIndex: '21' }}
                >
                    <div className="flex items-center flex-shrink-0 m-4 sm:m-6">
                        <Link to={"/home"}>
                            <MetriqLogoSVG width={200} className="hidden sm:block text-logo-primary" />
                        </Link>
                    </div>

                    <SpinningTitle className="hidden md:block flex-grow" />

                    <div className="flex items-center flex-shrink-0">
                        <UserMenu doLogout={signOut}/>
                        <Button className='lg:hidden ml-3' variant="outlined" onClick={() => setDrawerOpen(!drawerOpen)}>
                            <HiMenu className="h-5 w-5 p-0 m-0" />
                        </Button>
                    </div>
                </Navbar>

                <div className="flex flex-col min-h-screen">
                    <div className="flex flex-grow">
                        <SideBar handleClick={setDrawerOpen} drawerOpen={drawerOpen} />
                        <div className="flex-grow overflow-auto px-4 sm:px-6 lg:px-8">
                            {children}
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>
        </ProjectsListProvider>
    );
}

AuthenticatedLayout.propTypes = {
    children: PropTypes.node,
};

export default AuthenticatedLayout;